<template>
  <div>
    <b-card v-if="payment">
      <div class="d-flex justify-content-between align-items-start flex-wrap mb-2">
        <div>
          <h4 class="mb-25">
            Payment sous traitance #{{ payment.id }}
          </h4>
          <div class="text-muted">
            {{ companyLabel }} - {{ payment.orders_count || 0 }} colis
          </div>
        </div>
        <div class="d-flex align-items-center">
          <b-badge
            pill
            class="mr-1"
            :variant="payment.status === 'paye_recu' ? 'light-success' : 'light-warning'"
          >
            {{ payment.status_display || statusLabel(payment.status) }}
          </b-badge>
          <b-button
            v-if="payment.status !== 'paye_recu'"
            variant="success"
            :disabled="isValidating"
            @click="validateCurrentPayment"
          >
            <b-spinner
              v-if="isValidating"
              small
              class="mr-1"
            />
            <feather-icon
              v-else
              icon="CheckIcon"
              class="mr-1"
            />
            Valider payé reçu
          </b-button>
        </div>
      </div>

      <b-row class="mb-2">
        <b-col md="3">
          <strong>Total COD</strong>
          <div>{{ formatAmount(payment.total_cod_amount) }} TND</div>
        </b-col>
        <b-col md="3">
          <strong>Total à recevoir</strong>
          <div>{{ formatAmountAbsolute(payment.amount_to_receive) }} TND</div>
          <small class="text-muted">
            {{ payment.delivered_orders_count || 0 }} colis livrés ×
            {{ formatAmount(payment.subcontractor_delivery_price) }} - COD
          </small>
        </b-col>
        <b-col md="3">
          <strong>Créé par</strong>
          <div>{{ payment.created_by && payment.created_by.username ? payment.created_by.username : '-' }}</div>
        </b-col>
        <b-col md="3">
          <strong>Validé par</strong>
          <div>{{ payment.validated_by && payment.validated_by.username ? payment.validated_by.username : '-' }}</div>
        </b-col>
        <b-col md="3">
          <strong>Validé le</strong>
          <div>{{ formatDate(payment.validated_at) }}</div>
        </b-col>
      </b-row>

      <vue-good-table
        :columns="columns"
        :rows="orderRows"
        :search-options="{ enabled: true, placeholder: 'Rechercher un colis' }"
        :pagination-options="{ enabled: true, perPage: 25 }"
      >
        <template
          slot="table-row"
          slot-scope="props"
        >
          <span v-if="props.column.field === 'cod_amount'">
            {{ formatAmount(props.row.cod_amount) }} TND
          </span>
          <span v-else>{{ props.formattedRow[props.column.field] || '-' }}</span>
        </template>
      </vue-good-table>
    </b-card>
  </div>
</template>

<script>
import {
  BBadge,
  BButton,
  BCard,
  BCol,
  BRow,
  BSpinner,
} from 'bootstrap-vue'
import { mapActions, mapGetters } from 'vuex'
import { VueGoodTable } from 'vue-good-table'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  name: 'PaymentSousTraitanceShow',
  components: {
    BBadge,
    BButton,
    BCard,
    BCol,
    BRow,
    BSpinner,
    VueGoodTable,
  },
  props: {
    id: {
      type: [String, Number],
      required: true,
    },
  },
  data() {
    return {
      isValidating: false,
      columns: [
        { label: 'Code', field: 'order_id' },
        { label: 'Nom', field: 'name' },
        { label: 'Téléphone', field: 'phone1' },
        { label: 'Gouvernorat', field: 'governorate' },
        { label: 'Statut', field: 'status' },
        { label: 'COD', field: 'cod_amount' },
      ],
    }
  },
  computed: {
    ...mapGetters('paymentSousTraitance', ['payment']),
    companyLabel() {
      if (!this.payment || !this.payment.outsourcing_company) return '-'
      return this.payment.outsourcing_company.display_name || this.payment.outsourcing_company.name || '-'
    },
    orderRows() {
      if (!this.payment || !Array.isArray(this.payment.payment_orders)) return []
      return this.payment.payment_orders.map(row => ({
        id: row.id,
        order_id: row.order?.id || '-',
        name: row.order?.name || '-',
        phone1: row.order?.phone1 || '-',
        governorate: row.order?.governorate || '-',
        status: row.order?.status || '-',
        cod_amount: row.cod_amount,
      }))
    },
  },
  created() {
    this.fetchPayment(this.id)
  },
  methods: {
    ...mapActions('paymentSousTraitance', ['fetchPayment', 'validatePayment']),
    async validateCurrentPayment() {
      this.isValidating = true
      try {
        await this.validatePayment(this.id)
        this.showToast('success', 'Payment marqué payé reçu.')
      } catch (err) {
        const detail = err.response?.data?.detail || 'Validation impossible.'
        this.showToast('danger', detail)
      } finally {
        this.isValidating = false
      }
    },
    statusLabel(status) {
      return status === 'paye_recu' ? 'Payé reçu' : 'Brouillon'
    },
    formatDate(value) {
      return value ? new Date(value).toLocaleString('fr-TN') : '-'
    },
    formatAmount(value) {
      return Number(value || 0).toFixed(3)
    },
    formatAmountAbsolute(value) {
      return Math.abs(Number(value || 0)).toFixed(3)
    },
    showToast(variant, title) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title,
          icon: variant === 'success' ? 'CheckIcon' : 'AlertTriangleIcon',
          variant,
        },
      })
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-good-table.scss';
</style>
